// JEGY-DETAIL (full-page, belső nézet) — a `ticketGet` (TicketDetailSchema) MINDEN mezőjével.
//
// A LAP NYELVE: FORM-/DETAIL-oldal, mint az ügyfél-, fiók- és profil-oldal — `FormPage` (BALRA
// igazított, keskeny hasáb, saját görgetéssel) + `FormSection`-ök (NINCS kártya-keret és külön háttér; a
// tagolást a szekció-cím és a térköz adja). Korábban `PageBody` + `card-glass-lg` blokkok voltak, teljes
// oldalszélességben — az a LISTA-oldalak nyelve.
//
// SZEKCIÓ-SORREND (user-döntés, harmadik kör): a FEJLÉC hordozza az állapotot ÉS a vezérlőit
// (kattintható státusz-badge · felelős · support-csapat · Asana-feladat), majd Leírás · Mellékletek ·
// Fájl-megosztási linkek (csak a külső tárhelyek, ha van) · Kommentek.
//
// A FEJLÉC KÉT META-SORA (user-döntés): a cím alatt (1) MI EZ A JEGY — típus-badge · „ki nyitotta
// és mikor" · a fiók (nav-ikonnal), majd (2) HOGYAN KEZELJÜK — státusz-badge-választó · felelős · support-csapat.
// Egy sorban a hét elem tördelt, és a két olvasat amúgy is külön kérdés. A csoportokat TÉRKÖZ tagolja: pont
// csak az igénylő és a nyitás ideje között áll (azok egy gondolat). A logika: elöl a jegy TARTALMA, utána ami hozzá tapad (fájlok), és a végén a BESZÉLGETÉS — az
// nyílik a leghosszabbra, tehát a lap alján a helye (különben minden más a görgetés alá szorulna).
//
// AMI SZÁNDÉKOSAN NINCS:
//   • „Alapadatok" szekció — a fiók/igénylő/leadva a FEJLÉC meta-sorába került (kevés adat, sok címke volt:
//     egy külön szekció ott csak lassította az olvasást). A JEGYSZÁM egyáltalán nem ismétlődik (a topbar-morzsa
//     hordozza), és az ÜGYFÉL-név sem (egy jegy mindig fiókhoz tartozik, a fiók pedig egy ügyfélhez).
//   • „Kezelés" szekció — a státusz/felelős/csapat a FEJLÉC vezérlői (`TicketInlineControls`): a státusz-badge
//     maga a menü-trigger, a lezárás pedig dialógust nyit (ott kötelező a lezárás-ok). Eddig a státusz KÉTSZER
//     látszott: badge a fejlécben ÉS select a szekcióban.
//   • ragadós szekció-navigáció (`SectionNav`) — négy-öt szekcióhoz nem kell rail; a rail a hosszú, sok-blokkos
//     ügyfél-oldal eszköze.
//   • külön „Lezárás oka" SZEKCIÓ — a lezárás-ok a fejléc alatt, címkézve olvasható (`TicketCloseReason`), a
//     szerkesztése pedig a lezárás-dialógusban történik.
//
// Page-gate: `tickets.view`. Az e-mail/értesítés/partner-nézet KIHAGYVA (lásd `docs/modules/admin/tickets.md`).
import { type TicketDetailSchema } from '@atlas/contracts';
import {
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { AppShell } from '@/components/shared/app-shell';
import { EmptyState } from '@/components/shared/empty-state';
import { FormPage } from '@/components/shared/form-page';
import { FormSection } from '@/components/shared/form-section';
import { MetaDot } from '@/components/shared/meta-dot';
import { PersonChip } from '@/components/shared/person-chip';
import { TicketAsanaTask } from '@/components/tickets/ticket-asana-task';
import { TicketAttachments } from '@/components/tickets/ticket-attachments';
import { TicketComments } from '@/components/tickets/ticket-comments';
import {
  TicketCloseReason,
  TicketInlineControls,
} from '@/components/tickets/ticket-inline-controls';
import { TicketTypeBadge } from '@/components/tickets/ticket-type-badge';
import { Button } from '@/components/ui/button';
import { RichTextContent } from '@/components/ui/rich-text-content';
import { Skeleton } from '@/components/ui/skeleton';
import { ticketKeys } from '@/features/tickets/api';
import { useMarkTicketRead } from '@/features/tickets/use-mark-ticket-read';
import { TICKET_SECTION } from '@/features/tickets/sections';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { usePermissions } from '@/lib/use-permissions';
import { toHref } from '@/lib/external-url';
import { isForbiddenStatus } from '@/lib/error-status';

export const Route = createFileRoute('/tickets/$id')({
  beforeLoad: () => requireAuth(),
  component: TicketDetailPage,
});

type TicketDetail = z.infer<typeof TicketDetailSchema>;

/** ISO → lokalizált dátum+idő (a leadás pontos ideje a detailen érdekes, nem csak a nap). */
function fmtDateTime(iso: string | null, locale: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
}

/** Egy külső link-sor (a 3 tárhely + Asana) — ikonnal, új lapon nyílik. */
function LinkRow({ label, url }: { label: string; url: string }): JSX.Element {
  return (
    <a
      href={toHref(url)}
      target="_blank"
      rel="noreferrer"
      className="text-brand-hover flex items-center gap-1.5 truncate text-sm hover:underline"
    >
      <ArrowTopRightOnSquareIcon className="size-4 shrink-0" />
      <span className="truncate">{label}</span>
    </a>
  );
}

/**
 * A fiók weboldala IKON-LINKKÉNT, a fiók-név mellett. Miért nem `LinkRow`: a fejléc 1. sora négy adatot
 * hordoz (típus · fiók · igénylő · nyitás), és a kiírt URL egy ötödik, hosszú elemként tördelte a sort.
 * Az ikon a fiók-névhez TAPAD (nincs pont-elválasztó), így olvasatban a fiók tartozéka.
 */
function SiteLink({ url, label }: { url: string; label: string }): JSX.Element {
  return (
    <a
      href={toHref(url)}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      title={url}
      className="text-brand-hover shrink-0"
    >
      <ArrowTopRightOnSquareIcon className="size-3.5" />
    </a>
  );
}

export function TicketDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const navigate = useNavigate();
  const { can, isLoading: permsLoading } = usePermissions();
  const canView = can('tickets.view');
  const canManage = can('tickets.manage');

  const gated = !permsLoading && !canView;

  const detailKey = ticketKeys.detail(id);
  const detailQuery = api.ticketGet.useQuery(detailKey, { params: { id } });

  // OLVASOTTNAK JELÖLÉS a megnyitáskor (a lista „új" jelvénye a hívó utolsó megnyitásához mér) — egyszer
  // jegyenként, és csak ha a jegy ténylegesen betöltött (jog nélkül / 404-re nincs írás).
  const markRead = useMarkTicketRead();
  const loaded = detailQuery.data?.status === 200;
  useEffect(() => {
    if (loaded) markRead(id);
  }, [loaded, id, markRead]);
  const ticket: TicketDetail | undefined =
    detailQuery.data?.status === 200 ? detailQuery.data.body : undefined;
  const errStatus = detailQuery.error?.status;
  const isForbidden = gated || isForbiddenStatus(errStatus);

  const emDash = <span className="text-muted-foreground">—</span>;
  const backButton = (
    <Button variant="outline" onClick={() => void navigate({ to: '/tickets' })}>
      <ArrowLeftIcon className="h-4 w-4" />
      {t('tickets.back')}
    </Button>
  );

  if (!gated && detailQuery.isLoading) {
    return (
      <AppShell>
        <FormPage tight>
          <div className="space-y-6">
            <Skeleton className="h-8 w-72" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        </FormPage>
      </AppShell>
    );
  }

  if (isForbidden || !ticket) {
    return (
      <AppShell parent={{ label: t('nav.tickets'), to: '/tickets' }}>
        <FormPage tight>
          <EmptyState
            title={isForbidden ? t('tickets.forbidden') : t('tickets.notFound')}
            description={isForbidden ? t('tickets.forbiddenDesc') : t('tickets.notFoundDesc')}
            action={backButton}
          />
        </FormPage>
      </AppShell>
    );
  }

  // A Fájl-megosztási linkek CSAK a külső tárhelyek (Drive · OneDrive · Dropbox) — ahogy a legacyben. Az
  // Asana-feladat NEM ide tartozik (user-lelet: „Fájl-megosztási linkek" cím alatt csak egy Asana-gomb állt), az a
  // fejléc 2. sorában, a jegy kezelési vezérlői mellett él.
  const hasLinks =
    !!ticket.googleDriveLink || !!ticket.microsoftOnedriveLink || !!ticket.dropboxLink;

  const created = fmtDateTime(ticket.createdAt, locale);

  return (
    <AppShell
      parent={{ label: t('nav.tickets'), to: '/tickets' }}
      title={ticket.ticketNumber ?? ticket.subject}
    >
      <FormPage tight>
        <div className="space-y-8">
          {/* ══ FEJLÉC ══════════════════════════════════════════════════════════════════════════════════════
              A tárgy a főcím, alatta EGY meta-sor: státusz- és típus-badge, majd — halvány pont-elválasztókkal —
              a fiók (alatta az ügyfél), az igénylő és a leadás ideje. Ez váltotta le a külön „Alapadatok"
              szekciót (user-döntés): kevés adathoz sok címke volt, és a jegyszám (a morzsában már ott van)
              feleslegesen ismétlődött. A weboldal-link a fiók mellett áll — ott van értelme. */}
          <div className="space-y-2.5">
            <h1 className="text-2xl font-semibold">{ticket.subject}</h1>
            {/* 1. SOR — a jegy AZONOSÍTÓ adatai, HÁROM csoportban (user-döntés, 2026-08-25): típus-badge ·
                „ki nyitotta és mikor" · a fiók (ikonnal). A csoportok között TÉRKÖZ tagol, nem pont-elválasztó
                — pont csak az igénylő és a nyitás ideje között áll, mert azok EGY gondolatot mondanak. */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
              {ticket.type && (
                <TicketTypeBadge
                  type={ticket.type}
                  label={t(`tickets.typeLabel.${ticket.type}`, { defaultValue: ticket.type })}
                />
              )}
              {(ticket.requesterName !== null || ticket.requesterId !== null || created !== '') && (
                <span className="text-muted-foreground inline-flex min-w-0 items-center gap-x-2">
                  {/* Feloldatlan, de LÉTEZŐ igénylő-id = törölt személy — a blokk NEM tűnik el némán. */}
                  {(ticket.requesterName !== null || ticket.requesterId !== null) && (
                    <PersonChip
                      name={ticket.requesterName ?? t('common.deletedUser')}
                      givenName={ticket.requesterGivenName}
                      familyName={ticket.requesterFamilyName}
                      avatarUrl={ticket.requesterAvatarUrl}
                      size="sm"
                      className="min-w-0 gap-1.5 text-sm"
                    />
                  )}
                  {(ticket.requesterName !== null || ticket.requesterId !== null) &&
                    created !== '' && <MetaDot />}
                  {created !== '' && <span>{created}</span>}
                </span>
              )}
              {/* FIÓK — a nav-ból ismerős fiók-ikonnal (a sor végén: ez a jegy KONTEXTUSA). Az ÜGYFÉL-név
                  mellőle KIVEZETVE: egy jegy mindig fiókhoz tartozik, a fiók pedig egy ügyfélhez. A weboldal
                  ikon-linkként tapad a fiók-névhez, nem önálló meta-elemként. */}
              {ticket.accountName !== null && (
                <span className="text-muted-foreground inline-flex min-w-0 items-center gap-1.5">
                  <BuildingOffice2Icon className="size-3.5 shrink-0" aria-hidden />
                  <span className="min-w-0 truncate">{ticket.accountName}</span>
                  {ticket.accountWebsite !== null && (
                    <SiteLink url={ticket.accountWebsite} label={t('tickets.fields.website')} />
                  )}
                </span>
              )}
            </div>
            {/* 2. SOR — a jegy ÁLLAPOTA és annak vezérlői: kattintható státusz-badge · felelős · support-csapat
                (`TicketInlineControls`). A két sor szétválasztása user-döntés (2026-08-25): egy sorban a hét
                elem tördelt, és az „ilyen a jegy" / „így kezeljük" két különböző olvasat. */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
              <TicketInlineControls ticket={ticket} canManage={canManage} />
              {/* ASANA-FELADAT a kezelési vezérlők mellett (a legacyben is felül, Asana-jellel): létrehozás, vagy a
                  kötött feladat linkje + leválasztás. Jog nélkül és kötés nélkül nem jelenik meg. */}
              <TicketAsanaTask
                ticketId={id}
                version={ticket.version}
                asanaTaskUrl={ticket.asanaTaskUrl}
                canManage={canManage}
              />
            </div>
          </div>

          {/* ══ LEZÁRÁS OKA ══ ha van: a fejléc alatt, címkézve. A SZERKESZTÉSE a státusz-menü „Lezárva"
              ágán megnyíló dialógusban történik (ott kötelező mező) — külön szekció nem kell hozzá. */}
          <TicketCloseReason ticket={ticket} />

          {/* ══ LEÍRÁS ══ a nyitáskor rögzített rich-text (DOMPurify-sanitizálva: stored-XSS fal a migrált
              legacy tartalomra is). Utólag NEM szerkeszthető (legacy-paritás). */}
          <FormSection id={TICKET_SECTION.description} title={t('tickets.fields.description')}>
            {ticket.description ? <RichTextContent html={ticket.description} /> : emDash}
          </FormSection>

          {/* ══ MELLÉKLETEK ══ közvetlenül a leírás után (user-döntés): a fájlok a jegy TARTALMÁHOZ tapadnak,
              nem a beszélgetéshez. */}
          <TicketAttachments ticketId={id} canManage={canManage} />

          {/* ══ FÁJL-MEGOSZTÁSI LINKEK ══ csak ha van bármelyik (üres szekció nem információ). A mellékletek
              UTÁN: ugyanaz a téma (fájlok), csak külső tárhelyen. */}
          {hasLinks && (
            <FormSection id={TICKET_SECTION.links} title={t('tickets.links.title')}>
              <div className="space-y-2">
                {ticket.googleDriveLink && (
                  <LinkRow label={t('tickets.links.googleDrive')} url={ticket.googleDriveLink} />
                )}
                {ticket.microsoftOnedriveLink && (
                  <LinkRow label={t('tickets.links.onedrive')} url={ticket.microsoftOnedriveLink} />
                )}
                {ticket.dropboxLink && (
                  <LinkRow label={t('tickets.links.dropbox')} url={ticket.dropboxLink} />
                )}
              </div>
            </FormSection>
          )}

          {/* ══ KOMMENTEK ══ a lap ALJÁN (user-döntés): a szál nyílik a leghosszabbra, tehát bármi utána a
              görgetés alá szorulna. A fajta-váltó („Külső válasz" / „Belső jegyzet") a szekció FEJLÉC-SÁVJÁBAN. */}
          <TicketComments ticketId={id} accountId={ticket.accountId ?? ''} canManage={canManage} />
        </div>
      </FormPage>
    </AppShell>
  );
}
